if (!customElements.get('pdp-v5-rail')) {
  class PdpV5Rail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pdp-v5-usp__track');
      if (!this.track) return;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pauseReasons = new Set();
      this.inView = false;
      this.frame = 0;
      this.direction = 1;
      this.edgePause = 0;
      this.speed = Math.max(4, Math.min(20, Number(this.dataset.speed) || 8));
      this.onFrame = this.tick.bind(this);
      this.onRefresh = this.refresh.bind(this);
      this.onVisibility = () => this.updateAnimation();
      this.onEnter = (event) => {
        if (event.pointerType === 'mouse') this.pause('hover', true);
      };
      this.onLeave = () => this.pause('hover', false);
      this.onFocusIn = () => this.pause('focus', true);
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.pause('focus', false);
      };
      this.onPointerDown = () => this.pause('pointer', true);
      this.onPointerUp = () => {
        if (!this.pauseReasons.has('pointer')) return;
        this.pause('pointer', false);
        this.holdAfterInteraction();
      };
      this.onWheel = () => this.holdAfterInteraction();
      this.onBlockSelect = (event) => {
        if (event.detail.blockId === this.dataset.blockId) this.pause('editor', true);
      };
      this.onBlockDeselect = (event) => {
        if (event.detail.blockId === this.dataset.blockId) this.pause('editor', false);
      };
      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.track.addEventListener('wheel', this.onWheel, { passive: true });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.reducedMotion.addEventListener('change', this.onRefresh);
      this.resizeObserver = new ResizeObserver(this.onRefresh);
      this.resizeObserver.observe(this.track);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.updateAnimation();
      });
      this.visibilityObserver.observe(this);
      this.refresh();
      document.fonts?.ready.then(() => { if (this.isConnected) this.refresh(); });
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.interactionTimer);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.removeEventListener('pointerenter', this.onEnter);
      this.removeEventListener('pointerleave', this.onLeave);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.track?.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.reducedMotion?.removeEventListener('change', this.onRefresh);
    }

    pause(reason, paused) {
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
      this.updateAnimation();
    }

    holdAfterInteraction() {
      clearTimeout(this.interactionTimer);
      this.pause('interaction', true);
      this.interactionTimer = window.setTimeout(() => this.pause('interaction', false), 2500);
    }

    refresh() {
      this.stop();
      const previousPosition = this.track.scrollLeft;
      this.track.querySelectorAll('[data-rail-clone]').forEach((item) => item.remove());
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const overflows = this.maxScroll > 2;
      this.canAnimate = this.dataset.autoplay === 'true' && !this.reducedMotion.matches && this.track.children.length > 1 && overflows;
      this.position = overflows ? Math.max(0, Math.min(previousPosition, this.maxScroll)) : 0;
      if (!overflows) {
        this.direction = 1;
        this.edgePause = 0;
      } else if (this.position >= this.maxScroll) {
        this.direction = -1;
      } else if (this.position <= 0) {
        this.direction = 1;
      }
      this.track.scrollLeft = this.position;
      this.track.tabIndex = overflows ? 0 : -1;
      this.updateAnimation();
    }

    updateAnimation() {
      const running = this.canAnimate && this.inView && !document.hidden && this.pauseReasons.size === 0;
      if (!running) this.stop();
      else if (!this.frame) {
        const currentPosition = Math.max(0, Math.min(this.track.scrollLeft, this.maxScroll));
        if (Math.abs(currentPosition - this.position) > 1) this.edgePause = 0;
        this.position = currentPosition;
        if (this.position >= this.maxScroll) this.direction = -1;
        else if (this.position <= 0) this.direction = 1;
        this.frame = requestAnimationFrame(this.onFrame);
      }
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastTime = null;
    }

    tick(time) {
      this.frame = 0;
      let elapsed = this.lastTime === null ? 0 : Math.min(time - this.lastTime, 64);
      this.lastTime = time;
      if (this.edgePause > 0) {
        const pausedTime = Math.min(elapsed, this.edgePause);
        this.edgePause -= pausedTime;
        elapsed -= pausedTime;
      }
      if (elapsed > 0) {
        const nextPosition = this.position + this.direction * elapsed * this.speed / 1000;
        if (nextPosition >= this.maxScroll) {
          this.position = this.maxScroll;
          this.direction = -1;
          this.edgePause = 1200;
        } else if (nextPosition <= 0) {
          this.position = 0;
          this.direction = 1;
          this.edgePause = 1200;
        } else {
          this.position = nextPosition;
        }
      }
      this.track.scrollLeft = this.position;
      this.frame = requestAnimationFrame(this.onFrame);
    }
  }
  customElements.define('pdp-v5-rail', PdpV5Rail);
}

if (!customElements.get('pdp-v5-information')) {
  class PdpV5Information extends HTMLElement {
    connectedCallback() {
      // Preserve the native sticky component's actual position when a disclosure changes
      // its containing block. Its desired top can differ from the visible top at a boundary.
      this.onToggle = (event) => {
        if (!event.target.closest('summary') || !window.matchMedia('(min-width: 769px)').matches) return;
        const sticky = this.closest('.pdp-v5')?.querySelector('sticky-scroll-direction');
        if (!sticky?.container || !Number.isFinite(sticky.currentTop)) return;
        const visibleTop = sticky.container.getBoundingClientRect().top;
        if (visibleTop < sticky.defaultTop) {
          sticky.currentTop = visibleTop;
          sticky.scrollY = window.scrollY;
          sticky.container.style.top = `${visibleTop}px`;
        }
      };
      this.addEventListener('click', this.onToggle, true);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onToggle, true);
    }
  }
  customElements.define('pdp-v5-information', PdpV5Information);
}

if (!customElements.get('pdp-v5-quantity')) {
  const savedQuantities = new Map();

  class PdpV5Quantity extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.cc-main-product');
      this.productId = this.section?.querySelector('.pdp-v5')?.dataset.productId;
      this.mainInput = this.section?.querySelector('.js-product-form-main input[name="quantity"]');
      this.input = this.querySelector('input');
      this.addButton = this.section?.querySelector('.pdp-v5-sticky__add[type="submit"]');
      this.onMain = () => this.setQuantity(this.mainInput.value, false);
      this.onSticky = () => this.setQuantity(this.input.value, true);
      this.onButton = (event) => {
        const step = event.target.closest('button[data-step]')?.dataset.step;
        if (!step) return;
        this.input[step === 'plus' ? 'stepUp' : 'stepDown']();
        this.onSticky();
      };
      this.onSubmit = () => queueMicrotask(() => { if (this.addButton) this.addButton.disabled = true; });
      this.onCartDone = () => { if (this.addButton) this.addButton.disabled = false; };
      this.onStickySubmit = (event) => {
        const mainButton = this.section?.querySelector('.js-product-form-main [name="add"]');
        if (mainButton?.getAttribute('aria-disabled') === 'true') event.preventDefault();
        else this.setQuantity(this.input.value, true);
      };

      this.mainInput?.addEventListener('input', this.onMain);
      this.mainInput?.addEventListener('change', this.onMain);
      this.input.addEventListener('input', this.onSticky);
      this.input.addEventListener('change', this.onSticky);
      this.addEventListener('click', this.onButton);
      this.addButton?.addEventListener('click', this.onStickySubmit);
      this.mainInput?.form?.addEventListener('submit', this.onSubmit);
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((name) => {
        this.section?.addEventListener(name, this.onCartDone);
      });
      this.setQuantity(savedQuantities.get(this.productId) || this.mainInput?.value || 1, true);
    }

    disconnectedCallback() {
      this.mainInput?.removeEventListener('input', this.onMain);
      this.mainInput?.removeEventListener('change', this.onMain);
      this.input?.removeEventListener('input', this.onSticky);
      this.input?.removeEventListener('change', this.onSticky);
      this.removeEventListener('click', this.onButton);
      this.addButton?.removeEventListener('click', this.onStickySubmit);
      this.mainInput?.form?.removeEventListener('submit', this.onSubmit);
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((name) => {
        this.section?.removeEventListener(name, this.onCartDone);
      });
    }

    setQuantity(rawValue, updateMain) {
      const quantity = Math.min(999, Math.max(1, Number.parseInt(rawValue, 10) || 1));
      this.input.value = quantity;
      if (updateMain && this.mainInput) {
        this.mainInput.value = quantity;
        this.mainInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.productId) savedQuantities.set(this.productId, quantity);
    }
  }
  customElements.define('pdp-v5-quantity', PdpV5Quantity);
}

if (!window.bodycareV5ShareReady) {
  window.bodycareV5ShareReady = true;
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-v5-copy-link]');
    if (!button) return;
    try {
      await navigator.clipboard.writeText(button.dataset.shareUrl);
      button.textContent = 'Copied';
      const status = button.parentElement.querySelector('[data-v5-share-status]');
      if (status) status.textContent = 'Product link copied';
      window.setTimeout(() => { if (button.isConnected) button.textContent = 'Copy link'; }, 2000);
    } catch {
      const status = button.parentElement.querySelector('[data-v5-share-status]');
      if (status) status.textContent = 'Use a share option above';
    }
  });
}

// Spur renders the launcher in a shadow root with an inline bottom offset.
// Keep its existing position except while the V5 purchase bar is visible.
if (!window.bodycareV5ChatOffsetReady) {
  window.bodycareV5ChatOffsetReady = true;
  const section = document.querySelector('.cc-main-product .pdp-v5')?.closest('.cc-main-product');
  if (section) {
    let sticky;
    let chatPosition;
    let originalBottom;
    let stickyObserver;

    const syncChat = () => {
      if (!chatPosition) return;
      const active = sticky?.isConnected &&
        !sticky.classList.contains('sticky-atc-panel--out') &&
        !document.body.classList.contains('scrolled-to-bottom');
      const bottom = active ? (window.innerWidth <= 768 ? '140px' : '100px') : originalBottom;
      if (chatPosition.style.getPropertyValue('--lc-bottom') !== bottom) {
        chatPosition.style.setProperty('--lc-bottom', bottom);
      }
    };

    const bindSticky = () => {
      const current = document.querySelector('.cc-main-product .pdp-v5-sticky');
      if (current === sticky) return;
      stickyObserver?.disconnect();
      sticky = current;
      if (sticky) {
        stickyObserver = new MutationObserver(syncChat);
        stickyObserver.observe(sticky, { attributes: true, attributeFilter: ['class'] });
      }
      syncChat();
    };

    bindSticky();
    document.addEventListener('variant-change', bindSticky);
    document.addEventListener('shopify:section:load', bindSticky);
    new MutationObserver(syncChat).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', syncChat, { passive: true });

    let attempts = 0;
    const findChat = () => {
      chatPosition = document.getElementById('spur-live-chat')?.shadowRoot?.querySelector('.right');
      if (!chatPosition) return ++attempts >= 120;
      originalBottom = chatPosition.style.getPropertyValue('--lc-bottom') || '20px';
      new MutationObserver(syncChat).observe(chatPosition, { attributes: true, attributeFilter: ['style'] });
      syncChat();
      return true;
    };
    if (!findChat()) {
      const timer = window.setInterval(() => { if (findChat()) clearInterval(timer); }, 250);
    }
  }
}
